'use client';

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Session } from 'next-auth';
import { useSession, SessionProvider } from 'next-auth/react';
import { Bot, Upload, FileText, Zap, CheckCircle, Loader2, Shield, ShieldCheck, AlertTriangle } from 'lucide-react';
import { FeaturesSection } from './features-section';
import type { PlanOption } from '../lib/plans';
import { usePlanCatalog } from '../lib/use-plan-catalog';

// Interactive Hero section with full functionality
function InteractiveHeroSection({
    sessionRef,
    searchParams
}: {
    sessionRef: React.MutableRefObject<Session | null>;
    searchParams?: { content?: string; mode?: string };
}) {
    const { plans } = usePlanCatalog();
    const [inputText, setInputText] = useState('');
    const [outputText, setOutputText] = useState('');
    const [isHumanizing, setIsHumanizing] = useState(false);
    const [diffusionText, setDiffusionText] = useState('');
    const [diffusionChars, setDiffusionChars] = useState<Array<{ char: string, style: any }>>([]);
    const diffusionIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [isChecking, setIsChecking] = useState(false);
    const [wordCount, setWordCount] = useState(0);
    const [outputWordCount, setOutputWordCount] = useState(0);
    const [currentUserType, setCurrentUserType] = useState(0);
    const [usageStats, setUsageStats] = useState({ used: 0, limit: 1000, isFreeTier: true });
    const [documentId, setDocumentId] = useState<string | null>(null);
    const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);
    const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
    const [upgradeReason, setUpgradeReason] = useState<'limit_exceeded' | 'approaching_limit' | 'general_upgrade'>('general_upgrade');
    const [inputDetectionResult, setInputDetectionResult] = useState<{
        overall: { ai: number; human: number; mixed: number };
        sentences: Array<{
            sentence: string;
            index: number;
            ai: number;
            human: number;
            mixed: number;
            error?: string;
        }>;
    } | null>(null);
    const [outputDetectionResult, setOutputDetectionResult] = useState<{
        overall: { ai: number; human: number; mixed: number };
        sentences: Array<{
            sentence: string;
            index: number;
            ai: number;
            human: number;
            mixed: number;
            error?: string;
        }>;
    } | null>(null);

    // Initialize document on component mount
    useEffect(() => {
        const initializeDocument = async () => {
            // Only initialize if we don't have a document ID yet and have a site
            if (documentId || !sessionRef.current?.siteId) {
                return;
            }

            try {
                const siteId = sessionRef.current.siteId;
                console.log('Initializing empty document...');

                const response = await fetch(`/api/sites/${siteId}/aihumanit/documents`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ text: '' }) // Start with empty document
                });

                if (response.ok) {
                    const data = await response.json();
                    setDocumentId(data.documentId);
                    console.log('Empty document initialized:', data.documentId);
                } else {
                    console.warn('Failed to initialize document:', response.status);
                }
            } catch (error) {
                console.error('Error initializing document:', error);
            }
        };

        // Initialize document when component mounts or when siteId becomes available
        if (sessionRef.current?.siteId) {
            initializeDocument();
        }
    }, [sessionRef.current?.siteId, documentId]); // Re-run if siteId becomes available

    // Update word count when input changes (temporary calculation)
    useEffect(() => {
        const words = inputText.trim() ? inputText.trim().split(/\s+/).filter((w: string) => w.length > 0).length : 0;
        setWordCount(words);
    }, [inputText]);

    // Update existing document when user inputs substantial text
    useEffect(() => {
        const updateDocumentForText = async () => {
            // Only update document if we have substantial text, already have a document ID, and site
            if (!inputText.trim() || inputText.trim().length < 50 || !documentId || !sessionRef.current?.siteId) {
                return;
            }

            try {
                const siteId = sessionRef.current.siteId;
                console.log('Updating document with new text input...');

                // Use classify-sentences to update the document
                const response = await fetch(`/api/sites/${siteId}/aihumanit/classify-sentences`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        text: inputText,
                        documentId: documentId
                    })
                });

                if (response.ok) {
                    const data = await response.json();
                    // Note: Do NOT update wordCount here - data.usage.currentUsage is total monthly usage
                    // wordCount should remain the actual input text word count
                    console.log('Document updated successfully');
                } else {
                    console.warn('Failed to update document:', response.status);
                }
            } catch (error) {
                console.error('Error updating document:', error);
            }
        };

        // Debounce document update - wait 2 seconds after user stops typing
        const timeoutId = setTimeout(updateDocumentForText, 2000);
        return () => clearTimeout(timeoutId);
    }, [inputText, documentId, sessionRef]);

    // Update output word count
    useEffect(() => {
        const words = outputText.trim() ? outputText.trim().split(/\s+/).filter((w: string) => w.length > 0).length : 0;
        setOutputWordCount(words);
    }, [outputText]);

    // Load usage stats on component mount
    useEffect(() => {
        const loadUsageStats = async () => {
            // Wait for session to be available
            if (!sessionRef.current?.siteId) {
                console.warn('No siteId available yet, skipping usage stats load');
                return;
            }

            try {
                const siteId = sessionRef.current.siteId;
                console.log('Loading usage stats for siteId:', siteId);

                const response = await fetch(`/api/sites/${siteId}/aihumanit/usage`);

                if (response.ok) {
                    const data = await response.json();
                    console.log('Loaded usage stats:', data);

                    setUsageStats({
                        used: data.currentMonth.totalWordsBilled,
                        limit: data.subscription?.credits || 1000,
                        isFreeTier: !data.subscription || data.subscription.credits <= 1000
                    });

                    setSubscriptionInfo(data.subscription);

                    // Show upgrade prompt if needed
                    if (data.upgradeRecommended?.showUpgrade) {
                        console.log('Upgrade recommended:', data.upgradeRecommended.reason);
                    }
                } else {
                    console.warn('Failed to load usage stats:', response.status, response.statusText);
                }
            } catch (error) {
                console.error('Error loading usage stats:', error);
            }
        };

        // Load immediately if session is ready, otherwise wait a bit
        const timeoutId = setTimeout(loadUsageStats, 100);
        return () => clearTimeout(timeoutId);
    }, []);

    // Rotating user types
    const userTypes = ['college students', 'researchers', 'content writers', 'bloggers', 'marketers', 'influencers'];

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentUserType(prev => (prev + 1) % userTypes.length);
        }, 2000); // Change every 2 seconds

        return () => clearInterval(interval);
    }, []);

    const handleHumanize = async () => {
        if (!inputText.trim()) return;

        // Check usage limits
        const wordsToProcess = inputText.trim().split(/\s+/).filter((w: string) => w.length > 0).length;

        if (usageStats.isFreeTier && (usageStats.used + wordsToProcess) > usageStats.limit) {
            setUpgradeReason('limit_exceeded');
            setShowUpgradePrompt(true);
            return;
        }

        setIsHumanizing(true);
        setOutputText('');
        setDiffusionText('');
        setDiffusionChars([]);
        setInputDetectionResult(null);
        setOutputDetectionResult(null);

        try {
            const siteId = sessionRef.current?.siteId;
            if (!siteId) {
                console.error('No siteId available for humanization');
                setIsHumanizing(false);
                return;
            }
            console.log('Starting humanization process with siteId:', siteId);

            // Start continuous diffusion effect
            startContinuousDiffusion();

            // SIMPLIFIED FLOW: Only call rewrite endpoint
            // It handles: classification of input, rewriting, classification of output, and document updates
            console.log('Rewriting and processing text...');

            const rewriteResponse = await fetch(`/api/sites/${siteId}/aihumanit/rewrite`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    text: inputText,
                    documentId: documentId // Include documentId if we have one
                }),
            });

            if (rewriteResponse.status === 402) {
                // Usage limit exceeded
                const error = await rewriteResponse.json();
                console.warn('Usage limit exceeded:', error.details);
                setUpgradeReason('limit_exceeded');
                setShowUpgradePrompt(true);
                setIsHumanizing(false);
                // Stop diffusion animation
                if (diffusionIntervalRef.current) {
                    clearInterval(diffusionIntervalRef.current);
                    diffusionIntervalRef.current = null;
                }
                setDiffusionText('');
                setDiffusionChars([]);
                return;
            }

            if (rewriteResponse.ok) {
                const rewriteResult = await rewriteResponse.json();
                console.log('Rewrite result:', rewriteResult);

                // Extract data from the comprehensive response
                const humanizedText = rewriteResult.rewrittenText || 'No rewritten text received';

                // Set classification results from rewrite response
                setInputDetectionResult(rewriteResult.original);
                setOutputDetectionResult(rewriteResult.rewritten);

                // Store document ID if provided
                if (rewriteResult.documentId) {
                    setDocumentId(rewriteResult.documentId);
                    console.log('Document updated with ID:', rewriteResult.documentId);
                }

                // Update usage stats from response
                if (rewriteResult.usage) {
                    setUsageStats({
                        used: rewriteResult.usage.newTotal,
                        limit: rewriteResult.usage.limit || 1000,
                        isFreeTier: rewriteResult.usage.limit ? rewriteResult.usage.limit <= 1000 : true
                    });

                    // Note: Do NOT update wordCount here - it should remain the input text word count
                    // rewriteResult.usage.currentUsage is the total monthly usage, not current input word count
                }

                // All processing complete - show final result
                setOutputText(humanizedText);

                // Update usage stats with word count
                setUsageStats(prev => ({
                    ...prev,
                    used: prev.used + wordsToProcess
                }));

            } else {
                const errorText = await rewriteResponse.text();
                console.error('Rewrite error:', rewriteResponse.status, errorText);
                setOutputText(`Error (${rewriteResponse.status}): Failed to humanize text`);
            }
        } catch (error) {
            console.error('Humanization process failed:', error);
            setOutputText('Error: Failed to connect to humanization service');
        } finally {
            // Stop the diffusion effect and show final result
            setIsHumanizing(false);
            setDiffusionText('');
            setDiffusionChars([]);

            // Clear the diffusion interval
            if (diffusionIntervalRef.current) {
                clearInterval(diffusionIntervalRef.current);
                diffusionIntervalRef.current = null;
            }
        }
    };

    const handleImportFile = () => {
        // File import functionality
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.txt,.doc,.docx';
        input.onchange = (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    setInputText(e.target?.result as string);
                };
                reader.readAsText(file);
            }
        };
        input.click();
    };

    const handlePasteText = async () => {
        try {
            const text = await navigator.clipboard.readText();
            setInputText(text);
        } catch (error) {
            console.error('Failed to paste text:', error);
        }
    };

    const handleAIDetection = async () => {
        if (!inputText.trim()) return;

        setIsChecking(true);
        setInputDetectionResult(null);

        try {
            const siteId = sessionRef.current?.siteId || 'test-site-id';
            console.log('Running AI detection on input text with siteId:', siteId);

            const response = await fetch(`/api/sites/${siteId}/aihumanit/classify-sentences`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(
                    documentId
                        ? { documentId }
                        : { text: inputText }
                ),
            });

            if (response.ok) {
                const result = await response.json();
                console.log('Input AI detection result:', result);
                setInputDetectionResult(result);

                // Store document ID if returned and we don't have one yet
                if (result.documentId && !documentId) {
                    setDocumentId(result.documentId);
                    console.log('Document ID received from classification:', result.documentId);
                }
            } else {
                const errorText = await response.text();
                console.error('AI detection error:', response.status, errorText);
            }
        } catch (error) {
            console.error('AI detection failed:', error);
        } finally {
            setIsChecking(false);
        }
    };

    // Helper function to get background highlight color based on AI percentage
    // Gradient from red (100% AI) to green (0% AI) with transparency
    const getAIHighlightColor = (aiPercentage: number) => {
        const opacity = Math.max(0.3, Math.min(0.8, aiPercentage / 100 * 0.8 + 0.3)); // Scale opacity from 30% to 80%

        if (aiPercentage >= 50) {
            // Red gradient for high AI (50-100%)
            const intensity = ((aiPercentage - 50) / 50); // 0 to 1 scale
            return `rgba(239, 68, 68, ${opacity * intensity})`; // Red with variable opacity
        } else {
            // Green gradient for low AI (0-49%)
            const intensity = ((50 - aiPercentage) / 50); // 1 to 0 scale (inverted)
            return `rgba(34, 197, 94, ${opacity * intensity})`; // Green with variable opacity
        }
    };

    // Helper function to render text with inline sentence-level color highlighting and paragraph breaks
    const renderTextWithOverlays = (sentences: Array<{
        sentence: string;
        index: number;
        paragraphIndex?: number;
        isNewParagraph?: boolean;
        ai: number;
        human: number;
        mixed: number;
        error?: string;
    }>) => {
        if (!sentences || sentences.length === 0) return null;

        // Group sentences by paragraph if paragraph data is available
        const hasParagraphData = sentences.some(s => s.paragraphIndex !== undefined);

        if (hasParagraphData) {
            // Group by paragraph
            const paragraphs: Array<Array<typeof sentences[0]>> = [];
            sentences.forEach(sentence => {
                const paragraphIdx = sentence.paragraphIndex || 0;
                if (!paragraphs[paragraphIdx]) {
                    paragraphs[paragraphIdx] = [];
                }
                paragraphs[paragraphIdx].push(sentence);
            });

            return (
                <div className="whitespace-pre-wrap leading-relaxed text-gray-900">
                    {paragraphs.map((paragraphSentences, paragraphIndex) => (
                        <div key={paragraphIndex} className={paragraphIndex > 0 ? 'mt-4' : ''}>
                            {paragraphSentences.map((sentenceData, sentenceIndex) => (
                                <span
                                    key={sentenceData.index || sentenceIndex}
                                    className={`${sentenceData.error ? 'bg-gray-300' : ''} transition-all duration-300`}
                                    style={{
                                        backgroundColor: sentenceData.error ? undefined : getAIHighlightColor(sentenceData.ai),
                                        display: 'inline'
                                    }}
                                    title={sentenceData.error ? `Error: ${sentenceData.error}` :
                                        sentenceData.ai >= 50 ? `AI: ${sentenceData.ai}%` : `Human: ${100 - sentenceData.ai}%`}
                                >
                                    {sentenceData.sentence}{sentenceIndex < paragraphSentences.length - 1 ? ' ' : ''}
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            );
        } else {
            // Fallback to original rendering without paragraph structure
            return (
                <div className="whitespace-pre-wrap leading-relaxed text-gray-900">
                    {sentences.map((sentenceData, index) => (
                        <span
                            key={sentenceData.index || index}
                            className={`${sentenceData.error ? 'bg-gray-300' : ''} transition-all duration-300`}
                            style={{
                                backgroundColor: sentenceData.error ? undefined : getAIHighlightColor(sentenceData.ai),
                                display: 'inline'
                            }}
                            title={sentenceData.error ? `Error: ${sentenceData.error}` :
                                sentenceData.ai >= 50 ? `AI: ${sentenceData.ai}%` : `Human: ${100 - sentenceData.ai}%`}
                        >
                            {sentenceData.sentence}{index < sentences.length - 1 ? ' ' : ''}
                        </span>
                    ))}
                </div>
            );
        }
    };

    const handleCheckDetection = async () => {
        if (!outputText.trim()) return;

        setIsChecking(true);
        setOutputDetectionResult(null);

        try {
            const siteId = sessionRef.current?.siteId || 'test-site-id';
            console.log('Re-checking detection with siteId:', siteId);

            const response = await fetch(`/api/sites/${siteId}/aihumanit/classify-sentences`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ text: outputText }),
            });

            if (response.ok) {
                const result = await response.json();
                console.log('Detection result:', result);
                setOutputDetectionResult(result);
            } else {
                const errorText = await response.text();
                console.error('Detection error:', response.status, errorText);
            }
        } catch (error) {
            console.error('Detection check failed:', error);
        } finally {
            setIsChecking(false);
        }
    };

    // Progressive text diffusion - starts with many flickers, gradually stabilizes
    const startContinuousDiffusion = () => {
        const randomChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const textToAnimate = inputText.trim();

        if (!textToAnimate) {
            return;
        }

        let originalChars = textToAnimate.split('');
        let frame = 0;
        const startTime = Date.now();

        // Clear any existing interval
        if (diffusionIntervalRef.current) {
            clearInterval(diffusionIntervalRef.current);
        }

        const diffusionInterval = setInterval(() => {
            frame++;
            const elapsedSeconds = (Date.now() - startTime) / 1000;

            // Progressive flicker reduction: starts at 15%, reduces to 0.5% over 10 seconds
            const maxFlickerRate = 0.15; // Start with 15% flicker rate
            const minFlickerRate = 0.005; // End with 0.5% flicker rate
            const progressDuration = 10; // Reduce over 10 seconds

            const progress = Math.min(elapsedSeconds / progressDuration, 1);
            const currentFlickerRate = maxFlickerRate * (1 - progress) + minFlickerRate * progress;

            // Progressive diffusion - heavy at start, light at end
            const diffusedChars = originalChars.map((originalChar, index) => {
                if (originalChar === ' ' || originalChar === '\n') {
                    return { char: originalChar, style: {} };
                }

                // Character flickers based on current rate
                const shouldFlicker = Math.random() < currentFlickerRate;

                let displayChar = originalChar;
                let opacity = 1;

                if (shouldFlicker) {
                    // Brief flicker to random character
                    displayChar = randomChars[Math.floor(Math.random() * randomChars.length)];
                    opacity = 0.6 + Math.random() * 0.3; // Variable opacity for more natural effect
                }

                return {
                    char: displayChar,
                    style: {
                        opacity: opacity,
                        transition: 'opacity 0.12s ease-in-out'
                    }
                };
            });

            setDiffusionChars(diffusedChars);
        }, 150); // Faster updates for smoother progression

        // Store the interval reference
        diffusionIntervalRef.current = diffusionInterval;

        return diffusionInterval;
    };

    return (
        <section className="relative bg-gradient-to-br from-purple-50 via-white to-pink-50 overflow-hidden">
            {/* CSS Animation */}
            <style jsx>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>

            {/* Background decorative elements */}
            <div className="absolute inset-0">
                <div className="absolute top-20 right-20 w-72 h-72 bg-gradient-to-r from-purple-200 to-pink-200 rounded-full opacity-20 blur-3xl"></div>
                <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-r from-blue-200 to-purple-200 rounded-full opacity-20 blur-3xl"></div>
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-20">
                {/* Hero Header */}
                <div className="text-center mb-12">

                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                        Make Your AI Writing
                        <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent block sm:inline">
                            {' '}Sound Human
                        </span>
                    </h1>

                    {/* Call to Action Section */}
                    <div className="mb-12">
                        <div className="flex items-center justify-center gap-4 mb-6">
                            <div className="flex -space-x-3">
                                {/* Human profile pictures */}
                                <img
                                    src="/profile1.jpg"
                                    alt="User"
                                    className="w-12 h-12 rounded-full border-2 border-white shadow-sm blur-[2px] opacity-82 object-cover"
                                />
                                <img
                                    src="/profile2.jpg"
                                    alt="User"
                                    className="w-12 h-12 rounded-full border-2 border-white shadow-sm blur-[2px] opacity-82 object-cover"
                                />
                                <img
                                    src="/profile3.jpg"
                                    alt="User"
                                    className="w-12 h-12 rounded-full border-2 border-white shadow-sm blur-[2px] opacity-82 object-cover"
                                />
                                <img
                                    src="/profile4.jpg"
                                    alt="User"
                                    className="w-12 h-12 rounded-full border-2 border-white shadow-sm blur-[2px] opacity-82 object-cover"
                                />
                            </div>
                            <span className="text-gray-600 font-medium text-lg ml-4">
                                Join 50,000+{' '}
                                <span
                                    className="inline-block w-32 text-left"
                                    style={{ minWidth: '140px' }}
                                >
                                    <span
                                        key={currentUserType}
                                        className="inline-block transition-all duration-500 ease-in-out transform"
                                        style={{
                                            animation: 'fadeInUp 0.5s ease-in-out'
                                        }}
                                    >
                                        {userTypes[currentUserType]}
                                    </span>
                                </span>
                            </span>
                        </div>

                        <Button
                            className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-8 py-4 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                            asChild
                        >
                            <a href="/signup">
                                ✏️ Get started — for free
                            </a>
                        </Button>
                    </div>

                </div>

                {/* Two-pane interface - Main Focus */}
                <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden mb-16">
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 border-b border-gray-100">
                        <div className="flex items-center justify-between">
                            {/* Left spacer for balance */}
                            <div className="w-24"></div>

                            {/* Centered Title */}
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-md flex items-center justify-center">
                                    <Zap className="w-3.5 h-3.5 text-white" />
                                </div>
                                <h2 className="text-lg font-bold text-gray-900">
                                    Detect and Humanize AI Writing
                                </h2>
                                <div className="w-6 h-6 bg-gradient-to-r from-pink-500 to-purple-500 rounded-md flex items-center justify-center">
                                    <Shield className="w-3.5 h-3.5 text-white" />
                                </div>
                            </div>

                            {/* Usage Counter - Right */}
                            <div className="flex items-center gap-2 text-sm">
                                {/* Check if unlimited plan */}
                                {subscriptionInfo && subscriptionInfo.credits === null ? (
                                    /* Unlimited Plan - Gold Styling */
                                    <div className="flex items-center gap-2">
                                        <div className="px-3 py-1.5 rounded-full bg-gradient-to-r from-yellow-400 to-amber-500 text-white text-xs font-bold shadow-lg">
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-yellow-100">👑</span>
                                                <span>Unlimited</span>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    /* Free Tier or Limited Plan */
                                    <div className="flex items-center gap-2">
                                        <div className={`px-2 py-1 rounded-md text-xs font-medium ${usageStats.used / usageStats.limit > 0.8
                                            ? 'bg-red-50 text-red-700 border border-red-200'
                                            : 'bg-white text-gray-600 border border-gray-200'
                                            }`}>
                                            {usageStats.used.toLocaleString()}/{usageStats.limit?.toLocaleString() || '1000'}
                                        </div>

                                        {/* Show upgrade button based on usage or plan */}
                                        {(usageStats.used / usageStats.limit > 0.8 || !subscriptionInfo) && (
                                            <button
                                                className="relative px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-xs font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                                                onClick={() => {
                                                    if (usageStats.used / usageStats.limit > 0.8) {
                                                        setUpgradeReason('approaching_limit');
                                                    } else {
                                                        setUpgradeReason('general_upgrade');
                                                    }
                                                    setShowUpgradePrompt(true);
                                                }}
                                            >
                                                <div className="flex items-center gap-1">
                                                    <span>⚡ Upgrade</span>
                                                </div>
                                                {usageStats.used / usageStats.limit > 0.8 && (
                                                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>


                        {/* Transformation Display in Header */}
                        {inputDetectionResult && outputDetectionResult && (
                            <div className="mt-4 flex items-center justify-center">
                                <div className="bg-white rounded-lg shadow-md border border-gray-200 px-4 py-2">
                                    <div className="flex items-center space-x-4">
                                        {/* AI/Human Percentage - Left */}
                                        <div className={`flex items-center space-x-2 px-3 py-1 rounded-md text-xs font-bold ${inputDetectionResult.overall.ai >= 70
                                            ? 'bg-gradient-to-r from-red-500 to-rose-600 text-white' :
                                            inputDetectionResult.overall.ai <= 30
                                                ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white' :
                                                'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                                            }`}>
                                            <Shield className="w-3 h-3" />
                                            <span>
                                                {inputDetectionResult.overall.ai >= 50
                                                    ? `${inputDetectionResult.overall.ai}% AI`
                                                    : `${100 - inputDetectionResult.overall.ai}% Human`
                                                }
                                            </span>
                                        </div>

                                        {/* Arrow */}
                                        <div className="flex items-center space-x-1">
                                            <div className="w-3 h-px bg-gradient-to-r from-purple-400 to-pink-400"></div>
                                            <Zap className="w-4 h-4 text-purple-500" />
                                            <div className="w-3 h-px bg-gradient-to-r from-purple-400 to-pink-400"></div>
                                        </div>

                                        {/* AI/Human Percentage - Right */}
                                        <div className={`flex items-center space-x-2 px-3 py-1 rounded-md text-xs font-bold ${outputDetectionResult.overall.ai >= 70
                                            ? 'bg-gradient-to-r from-red-500 to-rose-600 text-white' :
                                            outputDetectionResult.overall.ai <= 30
                                                ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white' :
                                                'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                                            }`}>
                                            <span>
                                                {outputDetectionResult.overall.ai >= 50
                                                    ? `${outputDetectionResult.overall.ai}% AI`
                                                    : `${100 - outputDetectionResult.overall.ai}% Human`
                                                }
                                            </span>
                                            <ShieldCheck className="w-3 h-3" />
                                        </div>
                                    </div>

                                    {/* Success Message */}
                                    {inputDetectionResult.overall.ai >= 70 && outputDetectionResult.overall.ai <= 30 && (
                                        <div className="mt-2 text-center">
                                            <span className="text-xs font-medium text-emerald-600">✨ Transformation Successful! ✨</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="relative grid lg:grid-cols-2">

                        {/* Input pane */}
                        <div className="border-r border-gray-100 relative">
                            <div className="border-b border-gray-100 p-4 bg-gray-50">
                                <p className="text-gray-600 text-sm font-medium">
                                    Paste your AI-generated content here
                                </p>
                            </div>

                            <div className="p-6 relative">
                                {/* Show input with color overlays when we have detection results */}
                                {inputDetectionResult?.sentences && !isHumanizing ? (
                                    <div className="h-64 overflow-y-auto">
                                        <div className="mb-2 text-xs text-gray-500 font-medium">✨ Input Analysis (click to edit)</div>
                                        <div
                                            className="cursor-text"
                                            onClick={() => setInputDetectionResult(null)} // Clear results to allow editing
                                        >
                                            {renderTextWithOverlays(inputDetectionResult.sentences)}
                                        </div>
                                    </div>
                                ) : (
                                    <textarea
                                        value={inputText}
                                        onChange={(e) => setInputText(e.target.value)}
                                        placeholder="Enter your AI-generated text here..."
                                        className="w-full h-64 resize-none border-0 focus:ring-0 focus:outline-none text-gray-900 placeholder-gray-400 bg-transparent"
                                        disabled={isHumanizing}
                                    />
                                )}

                                {/* Sample suggestions - show when textarea is empty */}
                                {!inputText && !isHumanizing && (
                                    <div className="absolute inset-6 pointer-events-none">
                                        <div className="mt-8">
                                            <p className="text-gray-400 text-xs mb-3 font-medium">� Quick suggestions:</p>
                                            <div className="space-y-1">
                                                <button
                                                    onClick={() => setInputText("Free market economics is the idea that economic outcomes should be determined by voluntary exchanges between individuals and businesses with minimal interference from the government. At its core, it is built on the principles of supply and demand, competition, and private property rights. Buyers and sellers negotiate prices based on preferences, scarcity, and willingness to pay, creating a system where resources are allocated through countless decentralized decisions rather than central planning. Advocates argue this produces efficiency, innovation, and growth because businesses must continually adapt, lower costs, and improve quality to survive. When competition thrives, consumers benefit from better products, lower prices, and greater choice.")}
                                                    className="pointer-events-auto block w-full text-left px-2 py-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded border border-gray-200 hover:border-gray-300 transition-all cursor-pointer"
                                                    disabled={isHumanizing}
                                                >
                                                    <span className="text-xs">📚 "150 words on Free Market Economics"</span>
                                                </button>

                                                <button
                                                    onClick={() => setInputText("Climate change is one of the defining challenges of our time, reshaping the natural world and human societies alike. At its core, it is driven by the buildup of greenhouse gases in the atmosphere, primarily from the burning of fossil fuels, deforestation, and industrial activities. These emissions trap heat, disrupting Earth’s energy balance and pushing temperatures higher. The consequences extend far beyond warmer weather: melting ice caps raise sea levels, threatening coastal cities; shifting rainfall patterns bring droughts to some regions and floods to others; and ecosystems face pressures that drive species toward extinction. What makes climate change uniquely dangerous is not only its global reach but also its pace—natural systems and human infrastructure struggle to adapt quickly enough. Economically, climate-related disasters cost billions each year, from wildfires in North America to typhoons in Asia, undermining stability and growth. Socially, vulnerable populations—often in the Global South—bear the heaviest burdens, despite contributing least to the problem. Yet, the crisis also presents opportunities. Advances in renewable energy, green technology, and sustainable practices offer pathways to reduce emissions and build resilience. The real question is whether humanity can muster the political will and collective action to transition away from carbon dependence before crossing irreversible thresholds. Climate change is not a distant threat; it is a lived reality, demanding urgent and coordinated solutions that balance mitigation, adaptation, and justice. How we respond today will determine the livability of the planet for generations to come.")}
                                                    className="pointer-events-auto block w-full text-left px-2 py-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded border border-gray-200 hover:border-gray-300 transition-all cursor-pointer"
                                                    disabled={isHumanizing}
                                                >
                                                    <span className="text-xs">🌍 "300 words on Climate Change"</span>
                                                </button>

                                                <button
                                                    onClick={() => setInputText("Shakespeare's literary contributions to English literature demonstrate extraordinary artistic achievement through sophisticated narrative techniques and profound thematic exploration. His dramatic works exhibit complex character development, intricate plot structures, and masterful utilization of linguistic devices that continue to influence contemporary literary analysis and theatrical productions worldwide.")}
                                                    className="pointer-events-auto block w-full text-left px-2 py-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded border border-gray-200 hover:border-gray-300 transition-all cursor-pointer"
                                                    disabled={isHumanizing}
                                                >
                                                    <span className="text-xs">📖 "50 words on Shakespeare"</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="border-t border-gray-100 p-4 bg-gray-50">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleImportFile}
                                            className="flex items-center gap-2"
                                            disabled={isHumanizing}
                                        >
                                            <Upload className="w-4 h-4" />
                                            Import
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handlePasteText}
                                            className="flex items-center gap-2"
                                            disabled={isHumanizing}
                                        >
                                            <FileText className="w-4 h-4" />
                                            Paste
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleAIDetection}
                                            className="flex items-center gap-2"
                                            disabled={isChecking || isHumanizing || !inputText.trim()}
                                        >
                                            {isChecking ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Shield className="w-4 h-4" />
                                            )}
                                            {isChecking ? 'Checking...' : 'AI Detection'}
                                        </Button>
                                    </div>
                                    <span className="text-sm text-gray-500 font-medium">{wordCount} words</span>
                                </div>

                                <Button
                                    onClick={handleHumanize}
                                    disabled={isHumanizing || !inputText.trim()}
                                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-3"
                                >
                                    {isHumanizing ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            Humanizing...
                                        </>
                                    ) : (
                                        <>
                                            <Zap className="w-5 h-5 mr-2" />
                                            Humanize Text
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>

                        {/* Output pane */}
                        <div className="relative">
                            <div className="border-b border-gray-100 p-4 bg-gray-50">
                                <p className="text-gray-600 text-sm font-medium">
                                    Your humanized content appears here
                                </p>
                            </div>

                            <div className="p-6">
                                <div className="h-64 text-gray-900 overflow-y-auto">
                                    {isHumanizing ? (
                                        <div className="relative">
                                            {diffusionChars.length > 0 ? (
                                                <div className="relative">
                                                    {/* Subtle Text Diffusion Effect */}
                                                    <div className="whitespace-pre-wrap leading-relaxed text-sm">
                                                        <div className="text-gray-900">
                                                            {diffusionChars.map((charObj, index) => (
                                                                <span
                                                                    key={index}
                                                                    style={charObj.style}
                                                                >
                                                                    {charObj.char}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    {/* Simple processing indicator */}
                                                    <div className="absolute -bottom-6 left-0 flex items-center space-x-2 text-xs text-gray-500">
                                                        <div className="flex space-x-1">
                                                            <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></div>
                                                            <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '200ms' }}></div>
                                                            <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '400ms' }}></div>
                                                        </div>
                                                        <span className="font-medium">Processing text...</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-center h-full text-gray-500">
                                                    <div className="text-center">
                                                        <div className="relative">
                                                            <Zap className="w-8 h-8 mx-auto mb-4 text-purple-600 animate-bounce" />
                                                            <div className="absolute inset-0 w-8 h-8 mx-auto bg-purple-400 rounded-full animate-ping opacity-20"></div>
                                                        </div>
                                                        <p className="font-medium">Initializing AI humanization...</p>
                                                        <p className="text-sm text-gray-400 mt-1">Starting transformation</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : outputText ? (
                                        <div className="transition-opacity duration-500 ease-in-out opacity-100">
                                            {/* Show color-coded overlays if we have sentence data */}
                                            {outputDetectionResult?.sentences ?
                                                renderTextWithOverlays(outputDetectionResult.sentences) :
                                                <div className="whitespace-pre-wrap leading-relaxed">{outputText}</div>
                                            }
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-center h-full text-gray-400">
                                            <div className="text-center">
                                                <Bot className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                                <p>Your humanized content will appear here</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="border-t border-gray-100 p-4 bg-gray-50">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={!outputText}
                                            className="flex items-center gap-2"
                                            onClick={() => navigator.clipboard.writeText(outputText)}
                                        >
                                            <FileText className="w-4 h-4" />
                                            Copy
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={!outputText}
                                            className="flex items-center gap-2"
                                        >
                                            <Upload className="w-4 h-4" />
                                            Export
                                        </Button>
                                    </div>
                                    <span className="text-sm text-gray-500 font-medium">{outputWordCount} words</span>
                                </div>

                                <Button
                                    onClick={handleCheckDetection}
                                    disabled={isChecking || !outputText.trim()}
                                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 border border-gray-300"
                                >
                                    {isChecking ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            Checking...
                                        </>
                                    ) : (
                                        <>
                                            <Shield className="w-5 h-5 mr-2" />
                                            Re-check Detection
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Upgrade Prompt Modal */}
            {showUpgradePrompt && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowUpgradePrompt(false)}>
                    <div className="bg-white rounded-2xl p-8 max-w-lg mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Zap className="w-8 h-8 text-purple-600" />
                            </div>

                            {upgradeReason === 'limit_exceeded' ? (
                                <>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                                        Not enough words left
                                    </h3>
                                    <p className="text-gray-600 mb-6">
                                        You need {inputText.trim().split(/\s+/).filter((w: string) => w.length > 0).length} words but only have {usageStats.limit - usageStats.used} words remaining in your {subscriptionInfo ? 'plan' : 'free plan'}.
                                    </p>
                                </>
                            ) : upgradeReason === 'approaching_limit' ? (
                                <>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                                        You're running low on words
                                    </h3>
                                    <p className="text-gray-600 mb-6">
                                        You've used {usageStats.used.toLocaleString()} of your {usageStats.limit?.toLocaleString()} word limit this month. Upgrade to get more words and never worry about limits again.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                                        Upgrade your plan
                                    </h3>
                                    <p className="text-gray-600 mb-6">
                                        Get more words per month and unlock advanced features. Choose the plan that fits your needs.
                                    </p>
                                </>
                            )}
                        </div>

                        <div className="grid md:grid-cols-2 gap-4 mb-6">
                            {plans.filter(plan => plan.price > 0).map((plan: PlanOption) => (
                                <div
                                    key={plan.id}
                                    className={`rounded-lg p-4 relative ${plan.popular
                                            ? 'border-2 border-purple-500'
                                            : 'border border-gray-200'
                                        }`}
                                >
                                    {plan.popular && (
                                        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-purple-500 text-white px-2 py-1 rounded text-xs">
                                            Popular
                                        </div>
                                    )}
                                    <h4 className="font-semibold text-gray-900 mb-1">{plan.name}</h4>
                                    <p className="text-2xl font-bold text-gray-900 mb-2">
                                        ${plan.price}<span className="text-sm font-normal text-gray-600">/mo</span>
                                    </p>
                                    <p className="text-sm text-gray-600 mb-3">
                                        {plan.credits === -1 ? 'Unlimited words' : `${plan.credits.toLocaleString()} words/month`}
                                    </p>
                                    <Button
                                        className={plan.popular ? "w-full bg-purple-600 hover:bg-purple-700 text-white text-sm" : "w-full text-sm"}
                                        variant={plan.popular ? "default" : "outline"}
                                        asChild
                                    >
                                        <a href={`/payment?plan=${plan.id}&from=landing`}>
                                            {plan.name === 'Pro' ? 'Upgrade to Pro' : 'Go Unlimited'}
                                        </a>
                                    </Button>
                                </div>
                            ))}
                        </div>

                        <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => setShowUpgradePrompt(false)}
                        >
                            Maybe later
                        </Button>
                    </div>
                </div>
            )}
        </section>
    );
}

// Features section inspired by Phrasly
function HeroWithFeatures({ sessionRef, searchParams }: {
    sessionRef: React.MutableRefObject<Session | null>;
    searchParams?: { content?: string; mode?: string };
}) {
    return (
        <>
            <InteractiveHeroSection sessionRef={sessionRef} searchParams={searchParams} />
            <FeaturesSection />
        </>
    );
}

// Static version that shows while session is loading
function StaticHeroSection() {
    return (
        <section className="relative bg-gradient-to-br from-purple-50 via-white to-pink-50 overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute inset-0">
                <div className="absolute top-20 right-20 w-72 h-72 bg-gradient-to-r from-purple-200 to-pink-200 rounded-full opacity-20 blur-3xl"></div>
                <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-r from-blue-200 to-purple-200 rounded-full opacity-20 blur-3xl"></div>
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-20">
                {/* Hero Header */}
                <div className="text-center mb-12">

                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                        Make Your AI Writing
                        <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent block sm:inline">
                            {' '}Sound Human
                        </span>
                    </h1>

                    <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                        Transform your AI-generated content into natural, human-like writing that passes detection.
                        Perfect for essays, research papers, and professional content.
                    </p>

                    {/* Social proof - matching interactive version */}
                    <div className="flex items-center justify-center gap-4 mb-8">
                        <div className="flex -space-x-3">
                            <img
                                src="/profile1.jpg"
                                alt="User"
                                className="w-12 h-12 rounded-full border-2 border-white shadow-sm blur-[2px] opacity-82 object-cover"
                            />
                            <img
                                src="/profile2.jpg"
                                alt="User"
                                className="w-12 h-12 rounded-full border-2 border-white shadow-sm blur-[2px] opacity-82 object-cover"
                            />
                            <img
                                src="/profile3.jpg"
                                alt="User"
                                className="w-12 h-12 rounded-full border-2 border-white shadow-sm blur-[2px] opacity-82 object-cover"
                            />
                        </div>
                        <span className="text-gray-600 font-medium text-lg">
                            Join 50,000+ content writers
                        </span>
                    </div>
                </div>

                {/* Two-pane interface - loading state */}
                <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden mb-16 opacity-75">
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 border-b border-gray-100">
                        <h2 className="text-lg font-semibold text-center text-gray-700">
                            🎓 Transform Your Assignment Text
                        </h2>
                        <p className="text-sm text-center text-gray-500 mt-1">
                            Loading editor...
                        </p>
                    </div>
                    <div className="grid lg:grid-cols-2">
                        {/* Input pane */}
                        <div className="border-r border-gray-100">
                            <div className="border-b border-gray-100 p-4 bg-gray-50">
                                <p className="text-gray-400 text-sm font-medium">
                                    Loading editor...
                                </p>
                            </div>

                            <div className="p-6 relative">
                                <div className="w-full h-64 bg-gray-50 border border-gray-200 rounded relative overflow-hidden">
                                    <div className="absolute inset-0 p-4">
                                        <p className="text-gray-400 text-xs mb-3 font-medium">� Quick suggestions:</p>
                                        <div className="space-y-1">
                                            <div className="px-2 py-1 text-gray-400 rounded border border-gray-200 opacity-70">
                                                <span className="text-xs">📚 "150 words on Free Market Economics"</span>
                                            </div>

                                            <div className="px-2 py-1 text-gray-400 rounded border border-gray-200 opacity-70">
                                                <span className="text-xs">🌍 "300 words on Climate Change"</span>
                                            </div>

                                            <div className="px-2 py-1 text-gray-400 rounded border border-gray-200 opacity-70">
                                                <span className="text-xs">📖 "50 words on Shakespeare"</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                                        <div className="text-center">
                                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-purple-600" />
                                            <p className="text-sm text-gray-500">Initializing editor...</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="border-t border-gray-100 p-4 bg-gray-50">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex gap-2">
                                        <Button variant="outline" size="sm" disabled className="flex items-center gap-2">
                                            <Upload className="w-4 h-4" />
                                            Import
                                        </Button>
                                        <Button variant="outline" size="sm" disabled className="flex items-center gap-2">
                                            <FileText className="w-4 h-4" />
                                            Paste
                                        </Button>
                                    </div>
                                    <span className="text-sm text-gray-400 font-medium">0 words</span>
                                </div>

                                <Button disabled className="w-full bg-gray-300 text-gray-500 font-semibold py-3">
                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                    Loading...
                                </Button>
                            </div>
                        </div>

                        {/* Output pane */}
                        <div>
                            <div className="border-b border-gray-100 p-4 bg-gray-50">
                                <p className="text-gray-400 text-sm font-medium">
                                    Humanized content will appear here
                                </p>
                            </div>

                            <div className="p-6">
                                <div className="h-64 flex items-center justify-center text-gray-400">
                                    <div className="text-center">
                                        <Bot className="w-12 h-12 mx-auto mb-4 opacity-30" />
                                        <p>Preparing workspace...</p>
                                    </div>
                                </div>
                            </div>

                            <div className="border-t border-gray-100 p-4 bg-gray-50">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex gap-2">
                                        <Button variant="outline" size="sm" disabled className="flex items-center gap-2">
                                            <FileText className="w-4 h-4" />
                                            Copy
                                        </Button>
                                        <Button variant="outline" size="sm" disabled className="flex items-center gap-2">
                                            <Upload className="w-4 h-4" />
                                            Export
                                        </Button>
                                    </div>
                                    <span className="text-sm text-gray-400 font-medium">0 words</span>
                                </div>

                                <Button disabled className="w-full bg-gray-300 text-gray-500 font-semibold py-3">
                                    <CheckCircle className="w-5 h-5 mr-2" />
                                    Check for AI Detection
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Feature highlights */}
                <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto opacity-75">
                    <div className="text-center">
                        <div className="w-12 h-12 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="w-6 h-6 text-purple-600" />
                        </div>
                        <h3 className="font-semibold text-gray-700 mb-2">Bypass AI Detection</h3>
                        <p className="text-sm text-gray-500">
                            Advanced algorithms make your content undetectable by AI detection tools
                        </p>
                    </div>
                    <div className="text-center">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                            <Zap className="w-6 h-6 text-blue-600" />
                        </div>
                        <h3 className="font-semibold text-gray-700 mb-2">Instant Results</h3>
                        <p className="text-sm text-gray-500">
                            Get your humanized content in seconds, not minutes
                        </p>
                    </div>
                    <div className="text-center">
                        <div className="w-12 h-12 bg-gradient-to-r from-green-100 to-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                            <Bot className="w-6 h-6 text-green-600" />
                        </div>
                        <h3 className="font-semibold text-gray-700 mb-2">Preserve Meaning</h3>
                        <p className="text-sm text-gray-500">
                            Maintains the original message while improving readability
                        </p>
                    </div>
                </div>

                {/* Bottom text */}
                <div className="text-center mt-16">
                    <p className="text-gray-400 text-sm">
                        ✨ Trusted by students, writers, and content creators worldwide
                    </p>
                </div>
            </div>
        </section>
    );
}

// Main component following the noslead pattern
function HeroSectionWithSession({
    initialSession,
    searchParams
}: {
    initialSession: Session | null;
    searchParams?: { content?: string; mode?: string };
}) {
    const { data: session, status } = useSession();
    const sessionRef = useRef<Session | null>(initialSession);

    useEffect(() => {
        if (!session) {
            return;
        }
        if (session && sessionRef.current?.siteId !== session?.siteId) {
            sessionRef.current = session;
        }
    }, [session, status]);

    // Show static version while session is loading or document is initializing
    if (!sessionRef.current || !sessionRef.current.siteId) {
        return (
            <>
                <StaticHeroSection />
                <FeaturesSection />
            </>
        );
    }

    return <HeroWithFeatures sessionRef={sessionRef} searchParams={searchParams} />;
}

// Main export that wraps with SessionProvider
export function HeroSection({
    initialSession,
    searchParams
}: {
    initialSession: Session | null;
    searchParams?: { content?: string; mode?: string };
}) {
    return (
        <SessionProvider refetchOnWindowFocus={false} refetchInterval={0}>
            <HeroSectionWithSession initialSession={initialSession} searchParams={searchParams} />
        </SessionProvider>
    );
}
